import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Card, Button, Paper, IconButton, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FlightIcon from '@mui/icons-material/Flight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import dayjs from 'dayjs';
import { fetchPayroll, getVacationDays, fetchCurrentUserProfile, getAllTasksForUser } from '../api/services';
import { AuthContext } from '../context/auth-context';
import RequestTimeOffModal from '../components/RequestTimeOffModal';

const TEAL = '#009db9';
const TEAL_DARK = '#00707f';

const QUICK_LINKS = [
  { label: 'Employee Metrics', to: '/employee-metrics', icon: DashboardIcon },
  { label: 'Profile', to: '/profile', icon: PersonIcon },
  { label: 'Direct Deposit', to: '/direct-deposit', icon: AccountBalanceIcon },
  { label: 'Vacation', to: '/vacation', icon: FlightIcon },
];

const TARGET_POINTS = 60;

const getCurrentCycle = () => {
  const startDate = dayjs('2025-12-01');
  const today = dayjs();
  const daysDiff = today.diff(startDate, 'day');
  const cycleNumber = Math.floor(daysDiff / 14) + 1;
  const cycleStart = startDate.add((cycleNumber - 1) * 14, 'day');
  const cycleEnd = cycleStart.add(13, 'day');
  return {
    number: cycleNumber,
    display: `Cycle ${cycleNumber}: ${cycleStart.format('MMM D')}–${cycleEnd.format('D, YYYY')}`,
  };
};

const getEligibilityStatus = (progressPercentage) => {
  if (progressPercentage >= 120) return { status: 'Eligible +', color: 'success.main' };
  if (progressPercentage >= 91) return { status: 'Eligible', color: 'success.main' };
  if (progressPercentage >= 80) return { status: 'Potentially Eligible', color: 'warning.main' };
  return { status: 'Not Eligible', color: 'error.main' };
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const MetricTile = ({ title, value, showAmounts, icon: Icon }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      bgcolor: '#fff',
      borderRadius: '16px',
      border: '1px solid rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      justifyContent: 'center',
      boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s ease',
      '&:hover': { transform: 'translateY(-2px)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ bgcolor: 'rgba(0,157,185,0.1)', borderRadius: '10px', p: 0.7, display: 'flex' }}>
        <Icon sx={{ color: TEAL_DARK, fontSize: '1.1rem' }} />
      </Box>
      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: 'text.secondary', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </Typography>
    </Box>
    <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1.2rem', sm: '1.35rem' }, color: TEAL_DARK }}>
      {showAmounts ? (value ?? 'N/A') : '••••'}
    </Typography>
  </Paper>
);

// A labeled number block — used for the Used/Total pair under the donut.
const StatBlock = ({ label, value, align = 'left' }) => (
  <Box sx={{ textAlign: align }}>
    <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: { xs: '1.6rem', sm: '1.9rem' }, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: { xs: '0.8rem', sm: '0.85rem' }, mt: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

const VacationDonut = ({ unused, total, loading }) => {
  const size = 170;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const unusedFraction = !loading && total > 0 ? unused / total : 0;
  const unusedLength = c * unusedFraction;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, mx: 'auto' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth={stroke} />
        {!loading && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="#fff"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${unusedLength} ${c - unusedLength}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'stroke-dasharray 0.4s ease' }}
          />
        )}
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '2.6rem', lineHeight: 1 }}>
          {loading ? '...' : unused}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', mt: 0.5 }}>days unused</Typography>
      </Box>
    </Box>
  );
};

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const [showAmounts, setShowAmounts] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  // Payroll state
  const [payroll, setPayroll] = useState(null);
  const [payrollCritical, setPayrollCritical] = useState(false);
  const [payrollLoading, setPayrollLoading] = useState(true);

  // Vacation state
  const [vacation, setVacation] = useState({ unused: 0, used: 0, total: 0 });
  const [vacationCritical, setVacationCritical] = useState(false);
  const [vacationLoading, setVacationLoading] = useState(true);

  // Performance summary state — same source data as the Employee Metrics page
  const [metricsTasks, setMetricsTasks] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsCritical, setMetricsCritical] = useState(false);

  // Payroll fetch – 404 = no data, only real errors show message
  const loadPayroll = useCallback(async () => {
    setPayrollLoading(true);
    setPayrollCritical(false);
    try {
      const data = await fetchPayroll(0, 10);
      if (Array.isArray(data) && data.length > 0) {
        const latest = data.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];
        setPayroll(latest);
      } else {
        setPayroll(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setPayroll(null);
      } else if (!err.response || err.response.status >= 500) {
        setPayrollCritical(true);
      }
    } finally {
      setPayrollLoading(false);
    }
  }, []);

  // Vacation fetch – same logic
  const loadVacation = useCallback(async () => {
    setVacationLoading(true);
    setVacationCritical(false);
    try {
      const data = await getVacationDays();
      setVacation({
        unused: data.unusedDays ?? 0,
        used: data.usedDays ?? 0,
        total: data.totalDays ?? 0,
      });
    } catch (err) {
      if (err.response?.status !== 404 && (!err.response || err.response.status >= 500)) {
        setVacationCritical(true);
      }
    } finally {
      setVacationLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    setMetricsCritical(false);
    try {
      const profile = await fetchCurrentUserProfile();
      const userTasks = await getAllTasksForUser(profile.id, 0, 50, 'createdDate,desc');
      const tasksArray = Array.isArray(userTasks?.data?.content) ? userTasks.data.content : [];
      setMetricsTasks(tasksArray);
    } catch (err) {
      if (!err.response || err.response.status >= 500) {
        setMetricsCritical(true);
      }
      setMetricsTasks([]);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayroll();
  }, [loadPayroll]);

  useEffect(() => {
    loadVacation();
    const id = setInterval(loadVacation, 30000);
    return () => clearInterval(id);
  }, [loadVacation]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const toggleAmounts = () => setShowAmounts((p) => !p);
  const openRequest = () => setOpenModal(true);
  const closeRequest = () => setOpenModal(false);

  const fmtAmount = (n) => (n == null ? 'N/A' : `${n.toLocaleString()} KES`);
  const fmtDate = (s) => {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'N/A';
    const d = new Date(s);
    if (isNaN(d)) return 'N/A';
    const base = d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
    const day = d.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return base.replace(/\d+/, `${day}${suffix}`);
  };

  const daysToNextPay = (() => {
    if (!payroll?.currentPaymentDate || !/^\d{4}-\d{2}-\d{2}$/.test(payroll.currentPaymentDate)) return null;
    const target = new Date(payroll.currentPaymentDate);
    if (isNaN(target)) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  })();

  const totalStoryPoints = metricsTasks.reduce(
    (sum, task) => ((task.completed && task.status === 'Done') ? sum + (task.actualPoints || task.storyPoints || 0) : sum),
    0
  );
  const completedTasksCount = metricsTasks.filter((task) => task.completed && task.status === 'Done').length;
  const currentCycle = getCurrentCycle();
  const progressPercentage = (totalStoryPoints / TARGET_POINTS) * 100;
  const eligibility = getEligibilityStatus(progressPercentage);

  const cardSx = {
    borderRadius: '24px',
    background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
    boxShadow: '0 12px 30px rgba(0,112,127,0.2)',
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6f7', mt: { xs: 6, sm: 6, md: 8 } }}>
      <Box sx={{ maxWidth: '1200px', mx: 'auto', p: { xs: 1.5, sm: 2, md: 4 } }}>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 3, md: 3.5 } }}>

          {/* Hero */}
          <Card sx={{ ...cardSx, p: { xs: 3, sm: 3.5, md: 5 } }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', mb: 0.5 }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Typography>
            <Typography sx={{ fontWeight: 'bold', color: '#fff', fontSize: { xs: '1.6rem', sm: '2.1rem', md: '2.6rem' }, mb: { xs: 2.5, md: 3.5 } }}>
              {getGreeting()}, {user?.name || 'there'}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {QUICK_LINKS.map(({ label, to, icon: Icon }) => (
                <Box
                  key={to}
                  component={Link}
                  to={to}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.14)',
                    borderRadius: '14px',
                    p: 1.75,
                    textDecoration: 'none',
                    transition: 'background-color 0.2s ease',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                  }}
                >
                  <Icon sx={{ color: '#fff', fontSize: '1.3rem' }} />
                  <Typography sx={{ color: '#fff', fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 'bold' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: { xs: 2.5, sm: 3, md: 3.5 } }}>

            <Card sx={{ ...cardSx, p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', minHeight: 420 }}>
              <Typography sx={{ fontWeight: 'bold', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '1.3rem', mb: 3 }}>
                Vacation
              </Typography>

              {vacationCritical && (
                <Typography sx={{ color: '#fff', mb: 2, fontSize: '0.95rem' }}>
                  Unable to load vacation data. Please try again later.
                </Typography>
              )}

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
                <VacationDonut unused={vacation.unused} total={vacation.total} loading={vacationLoading} />

                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-around' }}>
                  <StatBlock label="days used" value={vacationLoading ? '...' : vacation.used} align="center" />
                  <Box sx={{ width: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <StatBlock label="days total" value={vacationLoading ? '...' : vacation.total} align="center" />
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={openRequest}
                sx={{
                  mt: 3,
                  bgcolor: '#fff',
                  color: TEAL_DARK,
                  fontWeight: 'bold',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  py: 1.2,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
                }}
              >
                Request time off
              </Button>
            </Card>

            <Card sx={{ ...cardSx, p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', minHeight: 420 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Typography sx={{ fontWeight: 'bold', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '1.3rem' }}>
                  Earnings
                </Typography>
                <IconButton onClick={toggleAmounts} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.14)' }} aria-label={showAmounts ? 'Hide amounts' : 'Show amounts'}>
                  {showAmounts ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Box>

              {payrollCritical && (
                <Typography sx={{ color: '#fff', mb: 2, fontSize: '0.95rem' }}>
                  Unable to load earnings data. Please try again later.
                </Typography>
              )}
              {!payroll && !payrollCritical && !payrollLoading && (
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 2, fontSize: '0.9rem' }}>
                  Payroll data will be available once your first payment is processed.
                </Typography>
              )}

              {payroll && daysToNextPay !== null && (
                <Chip
                  icon={<CalendarMonthIcon sx={{ color: '#fff !important', fontSize: '1.1rem' }} />}
                  label={
                    daysToNextPay > 0
                      ? `${daysToNextPay} day${daysToNextPay === 1 ? '' : 's'} to next payday`
                      : daysToNextPay === 0
                      ? 'Payday is today'
                      : 'Next payday recorded as passed'
                  }
                  sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', fontWeight: 600, fontSize: '0.8rem', mb: 3, alignSelf: 'flex-start' }}
                />
              )}

              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 2,
                width: '100%',
                mb: 2
              }}>
                <Box sx={{ 
                  flex: '0 0 calc(50% - 8px)',
                  minWidth: '0',
                  maxWidth: 'calc(50% - 8px)'
                }}>
                  <MetricTile 
                    title="Last Pay" 
                    value={payroll ? fmtDate(payroll.lastPaymentDate) : 'N/A'} 
                    showAmounts={showAmounts} 
                    icon={HistoryIcon} 
                  />
                </Box>
                <Box sx={{ 
                  flex: '0 0 calc(50% - 8px)',
                  minWidth: '0',
                  maxWidth: 'calc(50% - 8px)'
                }}>
                  <MetricTile 
                    title="Next Pay" 
                    value={payroll ? fmtDate(payroll.currentPaymentDate) : 'N/A'} 
                    showAmounts={showAmounts} 
                    icon={EventAvailableIcon} 
                  />
                </Box>
                <Box sx={{ 
                  flex: '0 0 calc(50% - 8px)',
                  minWidth: '0',
                  maxWidth: 'calc(50% - 8px)'
                }}>
                  <MetricTile 
                    title="Net Pay" 
                    value={payroll ? fmtAmount(payroll.grossPay) : 'N/A'} 
                    showAmounts={showAmounts} 
                    icon={AccountBalanceWalletIcon} 
                  />
                </Box>
                <Box sx={{ 
                  flex: '0 0 calc(50% - 8px)',
                  minWidth: '0',
                  maxWidth: 'calc(50% - 8px)'
                }}>
                  <MetricTile 
                    title="Bonus" 
                    value={payroll ? fmtAmount(payroll.bonus) : 'N/A'} 
                    showAmounts={showAmounts} 
                    icon={CardGiftcardIcon} 
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                  Tap the eye icon to show or hide amounts.
                </Typography>
                <Button
                  variant="contained"
                  component={Link}
                  to="/salary-metrics-chart"
                  startIcon={<TrendingUpIcon />}
                  sx={{
                    bgcolor: '#fff',
                    color: TEAL_DARK,
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
                  }}
                >
                  View more
                </Button>
              </Box>
            </Card>
          </Box>

          {/* Performance — summary of the Employee Metrics page */}
          <Card sx={{ ...cardSx, p: { xs: 3, sm: 3.5, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, mb: 3 }}>
              <Typography sx={{ fontWeight: 'bold', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '1.3rem' }}>
                Performance
              </Typography>
              <Chip
                label={currentCycle.display}
                sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', fontWeight: 600, fontSize: '0.8rem' }}
              />
            </Box>

            {metricsCritical && (
              <Typography sx={{ color: '#fff', mb: 2, fontSize: '0.95rem' }}>
                Unable to load performance data. Please try again later.
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr auto' }, gap: 3, alignItems: 'center' }}>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Story points
                </Typography>
                <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '2rem', mt: 0.5 }}>
                  {metricsLoading ? '...' : totalStoryPoints}
                  <Typography component="span" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', ml: 0.5 }}>
                    / {TARGET_POINTS}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Tasks completed
                </Typography>
                <Typography sx={{ color: '#fff', fontWeight: 'bold', fontSize: '2rem', mt: 0.5 }}>
                  {metricsLoading ? '...' : completedTasksCount}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Bonus status
                </Typography>
                <Typography sx={{ color: eligibility.color, fontWeight: 'bold', fontSize: '1.25rem', mt: 0.75 }}>
                  {metricsLoading ? '...' : eligibility.status}
                </Typography>
              </Box>
              <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, justifySelf: { xs: 'stretch', md: 'end' }, mt: { xs: 1, md: 0 } }}>
                <Button
                  variant="contained"
                  component={Link}
                  to="/employee-metrics"
                  endIcon={<ArrowForwardIcon />}
                  fullWidth={false}
                  sx={{
                    bgcolor: '#fff',
                    color: TEAL_DARK,
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    width: { xs: '100%', md: 'auto' },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.85)' },
                  }}
                >
                  View metrics
                </Button>
              </Box>
            </Box>
          </Card>

        </Box>
      </Box>

      <RequestTimeOffModal open={openModal} onClose={closeRequest} />
    </Box>
  );
};

export default HomePage;
